import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';

import styles from './Exams.module.scss';
import ExamsHeader from './ExamsHeader';
import ExamsOverview from './ExamsOverview';
import ExamGrid from './ExamGrid';
import ExamDetailPopup from './ExamDetailPopup';
import CreateExamModal from './CreateExamModal';
import { createAxios } from '../../createInstance';
import { loginSuccess } from '../../redux/authSlice';
import { getAllExamOnline } from '../../redux/apiRequest';

const cx = classNames.bind(styles);

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

function Exams() {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.login?.currentUser);
    const examState = useSelector((state) => state.exams?.exams);

    const axiosJWT = useMemo(() => {
        if (!currentUser) {
            return null;
        }
        return createAxios(currentUser, dispatch, loginSuccess);
    }, [currentUser, dispatch]);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedExam, setSelectedExam] = useState(null);

    const isFetching = Boolean(examState?.isFetching);

    const examSummary =
        examState?.examlists && typeof examState.examlists === 'object'
            ? examState.examlists
            : {};

    const examLists = Array.isArray(examSummary?.examLists) ? examSummary.examLists : [];

    const normalizedExams = useMemo(
        () =>
            examLists.map((exam, index) => ({
                id: exam?.id ?? exam?._id ?? `exam-${index}`,
                title: typeof exam?.title === 'string' ? exam.title : 'Khong ro ten',
                duration_seconds: toNumber(exam?.duration_seconds),
                submittedUsers: toNumber(exam?.submittedUsers),
                totalUsers: toNumber(exam?.totalUsers),
                createdAt: exam?.createdAt ?? null,
                updatedAt: exam?.updatedAt ?? null,
            })),
        [examLists],
    );

    const totalExams = Number.isFinite(Number(examSummary?.totalExams))
        ? Number(examSummary.totalExams)
        : normalizedExams.length;
    const totalSubjects = Number.isFinite(Number(examSummary?.totalSubjects))
        ? Number(examSummary.totalSubjects)
        : 0;

    const totalAssignments = useMemo(
        () => normalizedExams.reduce((sum, exam) => sum + exam.totalUsers, 0),
        [normalizedExams],
    );

    const submittedAssignments = useMemo(
        () => normalizedExams.reduce((sum, exam) => sum + exam.submittedUsers, 0),
        [normalizedExams],
    );

    const completionRate = Number.isFinite(Number(examSummary?.submissionRate))
        ? Number(examSummary.submissionRate)
        : totalAssignments === 0
            ? 0
            : (submittedAssignments / totalAssignments) * 100;

    const loadExams = useCallback(async () => {
        if (!currentUser?.accessToken || !axiosJWT) {
            return;
        }

        await getAllExamOnline(currentUser.accessToken, dispatch, axiosJWT);
    }, [axiosJWT, currentUser?.accessToken, dispatch]);

    useEffect(() => {
        loadExams();
    }, [loadExams]);

    const handleOpenCreateModal = useCallback(() => {
        setIsCreateModalOpen(true);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setIsCreateModalOpen(false);
    }, []);

    const handleExamCreated = useCallback(async () => {
        await loadExams();
    }, [loadExams]);

    const canCreateExam = Boolean(currentUser?.accessToken) && Boolean(axiosJWT);

    return (
        <section className={cx('exams-page')}>
            <ExamsHeader isRefreshing={isFetching} onRefresh={loadExams} />

            <ExamsOverview
                totalExams={totalExams}
                totalSubjects={totalSubjects}
                totalAssignments={submittedAssignments}
                completionRate={completionRate}
            />
            <ExamGrid
                exams={normalizedExams}
                isLoading={isFetching}
                onOpenCreate={canCreateExam ? handleOpenCreateModal : undefined}
                onSelectExam={(exam) => setSelectedExam(exam)}
            />

            {isCreateModalOpen && (
                <CreateExamModal
                    onClose={handleCloseCreateModal}
                    onCreated={handleExamCreated}
                    user={currentUser}
                    axiosJWT={axiosJWT}
                />
            )}
            {selectedExam && (
                <ExamDetailPopup
                    user={currentUser}
                    dispatch={dispatch}
                    axiosJWT={axiosJWT}
                    exam={selectedExam}
                    onClose={() => setSelectedExam(null)}
                />
            )}
        </section>
    );
}

export default Exams;
